import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HiMiniUserGroup } from "react-icons/hi2";
import { FaBars, FaSignOutAlt, FaClock, FaMoneyBillWave, FaCalendarTimes, FaBook, FaRegGrinBeam } from "react-icons/fa";
import { GrAnnounce } from "react-icons/gr";
import { MdSpaceDashboard } from "react-icons/md";
import { GiRuleBook } from "react-icons/gi";
import { IoCalendarSharp, IoDocument, IoSettingsSharp } from "react-icons/io5";
import { HiDocumentReport } from "react-icons/hi";
import { GrDocumentPerformance } from "react-icons/gr";
import { MdCoPresent, MdMenuBook, MdPayments } from "react-icons/md";
import { AiFillProfile, AiFillSchedule } from "react-icons/ai";
import { FaBalanceScale } from "react-icons/fa";
import { RiRefund2Fill } from "react-icons/ri";
import axios from 'axios';
import './sidebar.css';

const iconMap = {
  HiMiniUserGroup: <HiMiniUserGroup />,
  MdSpaceDashboard: <MdSpaceDashboard />,
  GrAnnounce: <GrAnnounce />,
  FaBook: <FaBook />,
  GiRuleBook: <GiRuleBook />,
  IoCalendarSharp: <IoCalendarSharp />,
  HiDocumentReport: <HiDocumentReport />,
  GrDocumentPerformance: <GrDocumentPerformance />,
  MdCoPresent: <MdCoPresent />,
  FaMoneyBillWave: <FaMoneyBillWave />,
  FaCalendarTimes: <FaCalendarTimes />,
  FaClock: <FaClock />,
  IoDocument: <IoDocument />,
  FaRegGrinBeam: <FaRegGrinBeam />,
  RiRefund2Fill: <RiRefund2Fill />,
  AiFillProfile: <AiFillProfile />,
  MdPayments: <MdPayments />,
  AiFillSchedule: <AiFillSchedule />,
  FaBalanceScale: <FaBalanceScale />,
  MdMenuBook: <MdMenuBook />,
  IoSettingsSharp: <IoSettingsSharp />,
  // Add missing icons that might be coming from backend
  FaClock: <FaClock />, // For overtime
  FcOvertime: <FaClock />, // If backend sends FcOvertime
  Overtime: <FaClock />, // If backend sends Overtime
  FCOvertime: <FaClock />, // Specifically for FCOvertime
};

const SideBar = ({ children, employeeName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [myInfoRoutes, setMyInfoRoutes] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mainRes, myInfoRes] = await Promise.all([
          axios.get('https://backend.ems.hiradentallab.com/api/panel-pages/panel/HRPanel'),
          axios.get('https://backend.ems.hiradentallab.com/api/panel-pages/my-info/HRPanel')
        ]);

        console.log('Main Routes Response:', mainRes.data);
        console.log('My Info Routes Response:', myInfoRes.data);

        const fetchedRoutes = mainRes.data.map(pp => {
          const pageData = pp.page || pp;
          return {
            path: pageData.path,
            name: pageData.name,
            icon: pageData.icon,
            subRoutes: pageData.subRoutes,
          };
        });

        const fetchedMyInfoRoutes = myInfoRes.data.map(item => {
          return {
            ...item,
            icon: item.icon,
          };
        });

        const filteredRoutes = fetchedRoutes.filter(route =>
          !fetchedMyInfoRoutes.some(myRoute => myRoute.name === route.name || myRoute.path === route.path)
        );

        setRoutes(filteredRoutes);
        setMyInfoRoutes(fetchedMyInfoRoutes);
      } catch (error) {
        console.error('Error fetching pages:', error);
      }
    };
    fetchData();
  }, []);

  const toggleSidebar = () => setIsOpen(!isOpen);

  const isActive = (path) => location.pathname.startsWith(path);

  const handleItemClick = (path, e) => {
    e.preventDefault();
    navigate(path);
    if (window.innerWidth <= 1024) setIsOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // Function to get the correct icon component
  const getIconComponent = (iconName) => {
    if (!iconName) return <FaClock />; // Default icon

    // Check if it's in our iconMap
    if (iconMap[iconName]) {
      return iconMap[iconName];
    }

    // Handle common icon names that might come from backend
    const iconNameLower = iconName.toLowerCase();
    if (iconNameLower.includes('overtime') || iconNameLower.includes('fcOvertime') || iconNameLower.includes('fcovertime')) {
      return <FaClock />;
    }
    if (iconNameLower.includes('dashboard')) {
      return <MdSpaceDashboard />;
    }
    if (iconNameLower.includes('user') || iconNameLower.includes('profile')) {
      return <HiMiniUserGroup />;
    }
    if (iconNameLower.includes('money') || iconNameLower.includes('payment')) {
      return <FaMoneyBillWave />;
    }
    if (iconNameLower.includes('calendar')) {
      return <IoCalendarSharp />;
    }
    if (iconNameLower.includes('document') || iconNameLower.includes('report')) {
      return <IoDocument />;
    }

    // Default fallback
    return <FaClock />;
  };

  return (
    <>
      <div className="sidebar-container">
        <button className="hamburger" onClick={toggleSidebar}>
          <FaBars />
        </button>
        <div className={`sidebar ${isOpen ? "open" : ""}`}>
          <section className="routes">
            {/* Main Routes */}
            {routes.map((route, index) => {
              const isRouteActive = isActive(route.path) ||
                (route.subRoutes && route.subRoutes.some(subRoute => isActive(subRoute.path)));
              return (
                <NavLink
                  to={route.path}
                  key={index}
                  className={`link ${isRouteActive ? 'active' : ''}`}
                  onClick={(e) => handleItemClick(route.path, e)}
                >
                  <div className="icon">{getIconComponent(route.icon)}</div>
                  <div className="link_text">{route.name}</div>
                </NavLink>
              );
            })}

            {/* My Info Section - Simple Heading with Pages */}
            {myInfoRoutes.length > 0 && (
              <div className="my-info-section">
                {/* My Info Heading */}
                <div className="my-info-heading">
                  <IoSettingsSharp className="icon" />
                  <span>My Info</span>
                </div>

                {/* My Info Pages */}
                {myInfoRoutes.map((item, index) => (
                  <NavLink
                    to={item.path}
                    key={index}
                    className={`link my-info-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={(e) => handleItemClick(item.path, e)}
                  >
                    <div className="icon">{getIconComponent(item.icon)}</div>
                    <div className="link_text">{item.name}</div>
                  </NavLink>
                ))}
              </div>
            )}
          </section>

          <div className="sidebar-footer">
            <span className="employee-name">{employeeName}</span>
            <button className="sign-out" onClick={handleLogout}>
              <FaSignOutAlt />
              {isOpen && "Sign Out"}
            </button>
          </div>
        </div>
      </div>
      <main>{children}</main>
    </>
  );
};

export default SideBar;